import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, CheckCircle, Pill, Plus, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const STORAGE_KEY = 'peer-plus-meds-reminders';

const getTodayKey = () => new Date().toISOString().slice(0, 10);

const createMedication = (overrides = {}) => ({
  id: `med-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  medicationName: 'PrEP / HIV meds',
  reminderTime: '21:00',
  enabled: true,
  notificationsEnabled: false,
  lastTakenDate: '',
  lastReminderDate: '',
  streak: 0,
  notes: '',
  takenHistory: [],
  ...overrides
});

const isYesterday = (dateValue) => {
  if (!dateValue) return false;
  const currentDay = new Date();
  currentDay.setHours(0, 0, 0, 0);
  const previousDay = new Date(currentDay);
  previousDay.setDate(previousDay.getDate() - 1);
  return dateValue === previousDay.toISOString().slice(0, 10);
};

const getDaysSince = (dateValue) => {
  if (!dateValue) return 0;
  const today = new Date(getTodayKey());
  const lastDate = new Date(dateValue);
  return Math.max(0, Math.floor((today - lastDate) / (1000 * 60 * 60 * 24)));
};

const getMissedDoses = (dateValue) => {
  if (!dateValue) return 0;
  return Math.max(0, getDaysSince(dateValue) - 1);
};

export default function MedsReminderPage() {
  const navigate = useNavigate();
  const [medications, setMedications] = useState([createMedication()]);
  const [activeMedicationId, setActiveMedicationId] = useState(null);

  useEffect(() => {
    const savedReminders = window.localStorage.getItem(STORAGE_KEY);
    if (!savedReminders) {
      return;
    }

    const parsedReminders = JSON.parse(savedReminders);
    if (Array.isArray(parsedReminders)) {
      setMedications(parsedReminders);
      setActiveMedicationId(parsedReminders[0]?.id || null);
      return;
    }

    const migratedReminder = createMedication(parsedReminders);
    setMedications([migratedReminder]);
    setActiveMedicationId(migratedReminder.id);
  }, []);

  useEffect(() => {
    if (!activeMedicationId && medications[0]) {
      setActiveMedicationId(medications[0].id);
    }
  }, [activeMedicationId, medications]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(medications));
  }, [medications]);

  useEffect(() => {
    const hasActiveReminders = medications.some((medication) => medication.enabled && medication.reminderTime);
    if (!hasActiveReminders) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      const currentTime = new Date().toTimeString().slice(0, 5);
      const todayKey = getTodayKey();

      setMedications((currentMedications) => currentMedications.map((medication) => {
        const dueNow = medication.enabled && medication.reminderTime === currentTime && medication.lastReminderDate !== todayKey;
        if (!dueNow) {
          return medication;
        }

        toast.success(`Time for ${medication.medicationName} 💊`);
        if (medication.notificationsEnabled && 'Notification' in window && Notification.permission === 'granted') {
          new Notification('PeeR+ meds reminder', {
            body: `Time for ${medication.medicationName}`
          });
        }

        return {
          ...medication,
          lastReminderDate: todayKey
        };
      }));
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [medications]);

  const activeMedication = useMemo(
    () => medications.find((medication) => medication.id === activeMedicationId) || medications[0],
    [activeMedicationId, medications]
  );

  const stats = useMemo(() => {
    const todayKey = getTodayKey();
    const recentDays = Array.from({ length: 7 }, (_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - index);
      return date.toISOString().slice(0, 10);
    });
    const weeklyCompletions = medications.reduce((sum, medication) => (
      sum + (medication.takenHistory || []).filter((day) => recentDays.includes(day)).length
    ), 0);
    const expectedWeeklyDoses = medications.filter((medication) => medication.enabled).length * 7;
    return {
      medicationCount: medications.length,
      completedToday: medications.filter((medication) => medication.lastTakenDate === todayKey).length,
      totalMissed: medications.reduce((sum, medication) => sum + getMissedDoses(medication.lastTakenDate), 0),
      weeklyAdherence: expectedWeeklyDoses > 0 ? Math.round((weeklyCompletions / expectedWeeklyDoses) * 100) : 0
    };
  }, [medications]);

  const missedDosePattern = useMemo(() => {
    const worstMedication = medications
      .map((medication) => ({
        name: medication.medicationName,
        missed: getMissedDoses(medication.lastTakenDate)
      }))
      .sort((a, b) => b.missed - a.missed)[0];

    if (!worstMedication || worstMedication.missed === 0) {
      return 'No obvious missed-dose pattern right now — nice.';
    }

    return `${worstMedication.name} has the most missed days lately (${worstMedication.missed}). That one may need a gentler routine.`;
  }, [medications]);

  const handleMedicationChange = (medicationId, field, value) => {
    setMedications((currentMedications) => currentMedications.map((medication) => (
      medication.id === medicationId ? { ...medication, [field]: value } : medication
    )));
  };

  const handleMarkTaken = (medicationId) => {
    const todayKey = getTodayKey();

    setMedications((currentMedications) => currentMedications.map((medication) => {
      if (medication.id !== medicationId) {
        return medication;
      }

      if (medication.lastTakenDate === todayKey) {
        toast.message('You already marked this dose today.');
        return medication;
      }

      const nextStreak = isYesterday(medication.lastTakenDate) ? medication.streak + 1 : 1;
      const nextHistory = Array.from(new Set([...(medication.takenHistory || []), todayKey]));
      return {
        ...medication,
        lastTakenDate: todayKey,
        streak: nextStreak,
        takenHistory: nextHistory
      };
    }));

    toast.success('Dose marked for today 💊');
  };

  const handleNotifications = async (medicationId, enabled) => {
    if (!enabled) {
      handleMedicationChange(medicationId, 'notificationsEnabled', false);
      return;
    }

    if (!('Notification' in window)) {
      toast.error('Browser notifications are not available here.');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      handleMedicationChange(medicationId, 'notificationsEnabled', true);
      toast.success('Notifications turned on.');
    } else {
      toast.error('Notifications were not allowed.');
    }
  };

  const handleAddMedication = () => {
    const nextMedication = createMedication({ medicationName: `Medication ${medications.length + 1}` });
    setMedications((currentMedications) => [...currentMedications, nextMedication]);
    setActiveMedicationId(nextMedication.id);
  };

  const handleRemoveMedication = (medicationId) => {
    if (medications.length === 1) {
      toast.error('Keep at least one medication card here.');
      return;
    }

    const filteredMedications = medications.filter((medication) => medication.id !== medicationId);
    setMedications(filteredMedications);
    setActiveMedicationId(filteredMedications[0]?.id || null);
  };

  const activeMissedDoses = activeMedication ? getMissedDoses(activeMedication.lastTakenDate) : 0;

  return (
    <div className="min-h-screen flex flex-col relative z-10" data-testid="meds-reminder-page">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="meds-reminder-back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-3xl emoji-bounce">💊</span>
              <h1 className="text-2xl font-bold text-gradient" data-testid="meds-reminder-title">
                PrEP / HIV Med Reminder
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        <section className="mb-8 rounded-[2rem] border border-[rgb(var(--primary))]/20 bg-gradient-to-br from-[rgb(var(--primary))]/16 to-[rgb(var(--secondary))]/10 p-6" data-testid="meds-reminder-hero">
          <div className="text-5xl mb-4">⏰</div>
          <h2 className="text-3xl font-semibold text-[rgb(var(--text-primary))] mb-3">Keep track of more than one med.</h2>
          <p className="text-sm sm:text-base text-[rgb(var(--text-secondary))] leading-relaxed">
            Add multiple meds, keep reminder times separate, mark doses done, and catch missed days without the shame spiral.
          </p>
        </section>

        <section className="grid gap-4 sm:grid-cols-3 mb-8" data-testid="meds-reminder-stats">
          <div className="rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-5">
            <div className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))]">Medications</div>
            <div className="mt-2 text-3xl font-semibold text-[rgb(var(--text-primary))]" data-testid="meds-reminder-med-count">{stats.medicationCount}</div>
            <p className="mt-2 text-sm text-[rgb(var(--text-secondary))]">Multiple meds supported now.</p>
          </div>
          <div className="rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-5">
            <div className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))]">Done today</div>
            <div className="mt-2 text-3xl font-semibold text-[rgb(var(--text-primary))]" data-testid="meds-reminder-complete-count">{stats.completedToday}</div>
            <p className="mt-2 text-sm text-[rgb(var(--text-secondary))]">Marked complete today.</p>
          </div>
          <div className="rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-5">
            <div className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))]">Missed doses</div>
            <div className="mt-2 text-3xl font-semibold text-[rgb(var(--text-primary))]" data-testid="meds-reminder-missed-count">{stats.totalMissed}</div>
            <p className="mt-2 text-sm text-[rgb(var(--text-secondary))]">Across your current meds.</p>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2 mb-8" data-testid="meds-reminder-weekly-summary">
          <div className="rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-5">
            <div className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))]">Weekly summary</div>
            <div className="mt-2 text-3xl font-semibold text-[rgb(var(--text-primary))]" data-testid="meds-reminder-weekly-adherence">{stats.weeklyAdherence}%</div>
            <p className="mt-2 text-sm text-[rgb(var(--text-secondary))]">Approximate adherence across the last 7 days.</p>
          </div>
          <div className="rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-5">
            <div className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))]">Missed-dose pattern</div>
            <p className="mt-2 text-sm leading-relaxed text-[rgb(var(--text-secondary))]" data-testid="meds-reminder-pattern-copy">{missedDosePattern}</p>
          </div>
        </section>

        <section className="mb-6" data-testid="meds-reminder-medication-tabs">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h3 className="text-lg font-semibold text-[rgb(var(--text-primary))]">Your medications</h3>
            <Button onClick={handleAddMedication} data-testid="add-medication-button" className="rounded-full party-gradient text-white">
              <Plus size={18} weight="bold" className="mr-2" />
              Add medication
            </Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {medications.map((medication) => (
              <button
                key={medication.id}
                type="button"
                onClick={() => setActiveMedicationId(medication.id)}
                data-testid={`medication-tab-${medication.id}`}
                className={`rounded-[2rem] border p-4 text-left transition-colors ${
                  medication.id === activeMedication?.id
                    ? 'border-[rgb(var(--primary))]/40 bg-[rgb(var(--primary))]/10'
                    : 'border-white/10 bg-[rgb(var(--surface))]'
                }`}
              >
                <div className="text-base font-semibold text-[rgb(var(--text-primary))]">{medication.medicationName}</div>
                <p className="mt-1 text-sm text-[rgb(var(--text-secondary))]">{medication.reminderTime || 'No time set'} • {medication.enabled ? 'On' : 'Paused'}</p>
              </button>
            ))}
          </div>
        </section>

        {activeMedication && (
          <section className="rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-6 mb-8" data-testid="meds-reminder-settings-card">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="medication-name-input">Medication name</Label>
                <Input
                  id="medication-name-input"
                  value={activeMedication.medicationName}
                  onChange={(event) => handleMedicationChange(activeMedication.id, 'medicationName', event.target.value)}
                  data-testid="medication-name-input"
                  className="rounded-2xl border-white/10 bg-[rgb(var(--surface-elevated))]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="medication-time-input">Reminder time</Label>
                <Input
                  id="medication-time-input"
                  type="time"
                  value={activeMedication.reminderTime}
                  onChange={(event) => handleMedicationChange(activeMedication.id, 'reminderTime', event.target.value)}
                  data-testid="medication-time-input"
                  className="rounded-2xl border-white/10 bg-[rgb(var(--surface-elevated))]"
                />
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-[rgb(var(--surface-elevated))] px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-[rgb(var(--text-primary))]">Daily reminder</p>
                  <p className="text-xs text-[rgb(var(--text-secondary))]">Shows a nudge when the app is open</p>
                </div>
                <Switch
                  checked={activeMedication.enabled}
                  onCheckedChange={(value) => handleMedicationChange(activeMedication.id, 'enabled', value)}
                  data-testid="meds-reminder-enabled-switch"
                />
              </div>
              <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-[rgb(var(--surface-elevated))] px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-[rgb(var(--text-primary))]">Browser notifications</p>
                  <p className="text-xs text-[rgb(var(--text-secondary))]">Optional if your browser allows them</p>
                </div>
                <Switch
                  checked={activeMedication.notificationsEnabled}
                  onCheckedChange={(value) => handleNotifications(activeMedication.id, value)}
                  data-testid="meds-reminder-notifications-switch"
                />
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <Label htmlFor="medication-notes-input">Helpful note</Label>
              <textarea
                id="medication-notes-input"
                value={activeMedication.notes}
                onChange={(event) => handleMedicationChange(activeMedication.id, 'notes', event.target.value)}
                data-testid="medication-notes-input"
                rows={3}
                placeholder="Example: keep meds near water, or take after dinner."
                className="w-full rounded-2xl border border-white/10 bg-[rgb(var(--surface-elevated))] p-4 text-sm text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-secondary))] focus:outline-none focus:border-[rgb(var(--primary))]/50 resize-none"
              />
            </div>

            {activeMissedDoses > 0 && (
              <div className="mt-6 rounded-2xl border border-amber-400/20 bg-amber-500/10 p-4" data-testid="meds-reminder-missed-warning">
                <p className="text-sm text-amber-200 leading-relaxed">
                  Looks like you missed about {activeMissedDoses} dose{activeMissedDoses > 1 ? 's' : ''} here. No shame — if you need advice about a missed dose, check your clinic/pharmacist guidance or ask Nad for support.
                </p>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => handleMarkTaken(activeMedication.id)} data-testid="mark-dose-taken-button" className="rounded-full party-gradient text-white">
                <CheckCircle size={18} weight="fill" className="mr-2" />
                Mark today’s dose done
              </Button>
              <Button
                variant="outline"
                onClick={() => handleRemoveMedication(activeMedication.id)}
                data-testid="remove-medication-button"
                className="rounded-full border-white/10 text-[rgb(var(--text-secondary))] hover:bg-white/5"
              >
                <Trash size={16} weight="duotone" className="mr-2" />
                Remove medication
              </Button>
            </div>
          </section>
        )}

        <section className="rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-5 mb-8" data-testid="meds-reminder-today-list">
          <h3 className="text-lg font-semibold text-[rgb(var(--text-primary))] mb-4">Today at a glance</h3>
          <div className="space-y-3">
            {medications.map((medication) => {
              const missedDoses = getMissedDoses(medication.lastTakenDate);
              return (
                <div key={medication.id} className="rounded-2xl border border-white/5 bg-[rgb(var(--surface-elevated))] px-4 py-3" data-testid={`meds-summary-${medication.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-sm font-semibold text-[rgb(var(--text-primary))]">{medication.medicationName}</div>
                      <div className="text-xs text-[rgb(var(--text-secondary))] mt-1">{medication.reminderTime || 'No time'} • streak {medication.streak}</div>
                    </div>
                    <div className="text-xs text-[rgb(var(--text-secondary))]">
                      {medication.lastTakenDate === getTodayKey() ? 'Done today' : missedDoses > 0 ? `${missedDoses} missed` : 'Pending today'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2" data-testid="meds-reminder-support-cards">
          <div className="rounded-[2rem] border border-[rgb(var(--primary))]/20 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 p-5">
            <div className="flex items-center gap-2 mb-3 text-[rgb(var(--text-primary))]">
              <Bell size={18} weight="duotone" />
              <h3 className="font-semibold">Reminder tip</h3>
            </div>
            <p className="text-sm text-[rgb(var(--text-secondary))] leading-relaxed">
              For stronger background reminders, install PeeR+ on your phone or desktop once PWA mode is available in your browser.
            </p>
          </div>
          <div className="rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-5">
            <div className="flex items-center gap-2 mb-3 text-[rgb(var(--text-primary))]">
              <Pill size={18} weight="duotone" />
              <h3 className="font-semibold">Need support staying on track?</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate('/chat?action=emotional_support')}
                data-testid="meds-reminder-chat-button"
                className="rounded-full party-gradient text-white"
              >
                Ask Nad for support
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/living-with-hiv#treatment')}
                data-testid="meds-reminder-living-with-hiv-button"
                className="rounded-full border-[rgb(var(--primary))]/40 text-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/10"
              >
                Open treatment tips
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}